import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  updateRateLimitsSchema,
  type UpdateRateLimitsInput,
} from '@/lib/schemas/ai-schemas';
import type { AIProvider } from '@/services/admin-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

interface RateLimitsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  provider: AIProvider | null;
  onSubmit: (data: UpdateRateLimitsInput) => void;
  isPending: boolean;
}

export function RateLimitsDialog({ isOpen, onClose, provider, onSubmit, isPending }: RateLimitsDialogProps) {
  const form = useForm<z.input<typeof updateRateLimitsSchema>, unknown, UpdateRateLimitsInput>({
    resolver: zodResolver(updateRateLimitsSchema),
  });

  useEffect(() => {
    if (provider) {
      form.reset({
        rpmLimit: provider.rpmLimit ?? undefined,
        rpsLimit: provider.rpsLimit ?? undefined,
      });
    }
  }, [provider, form]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="rate-limits-dialog">
        <DialogHeader>
          <DialogTitle>Rate Limits</DialogTitle>
          <DialogDescription>
            Update rate limits for <strong>{provider?.name}</strong>. Leave empty for unlimited.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4"
        >
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="rl-rpm">RPM Limit</Label>
              <Input id="rl-rpm" type="number" placeholder="e.g. 60" {...form.register('rpmLimit')} data-testid="rl-rpm" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rl-rps">RPS Limit</Label>
              <Input id="rl-rps" type="number" placeholder="e.g. 10" {...form.register('rpsLimit')} data-testid="rl-rps" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
